import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from '../components/Link.js';
import { ButtonLink, Logo, cx } from '../design/index.js';

/**
 * The site's navigation.
 *
 * Sticky, 64px, no shadow: a rule under it, on the colour of the salon wall
 * the page is set on. The anchors are real fragment links, so the page works
 * before any JavaScript runs and a section link can be sent to somebody.
 *
 * `signedIn` changes one control and nothing else. A visitor who already has a
 * session should be offered their dashboard rather than a sign-in form; being
 * shown "Connexion" while logged in is the smallest possible sign that a
 * product is not paying attention.
 */

const SECTIONS: readonly (readonly [string, string])[] = [
  ['#fonctionnement', 'Fonctionnement'],
  ['#capacites', 'Ce qu’elle fait'],
  ['#integrations', 'Intégrations'],
  ['#securite', 'Sécurité'],
  ['#tarifs', 'Tarifs'],
];

export function SiteHeader({
  signedIn,
  loginUrl,
  startUrl,
}: {
  readonly signedIn: boolean;
  readonly loginUrl: string;
  /**
   * Where the main button leads: creating an account, or the dashboard for
   * somebody already signed in. It says « Créer mon compte » rather than
   * « Essayer »: there is no free trial, and a button should not promise one.
   */
  readonly startUrl: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-wall-line bg-wall">
      <div className="shell flex h-16 items-center justify-between gap-6 lg:h-[72px]">
        <Link href="/" className="inline-flex rounded-control">
          <Logo tone="light" />
          <span className="sr-only">Accueil</span>
        </Link>

        <nav aria-label="Sections du site" className="hidden lg:block">
          <ul className="flex items-center gap-6">
            {SECTIONS.map(([href, label]) => (
              <li key={href}>
                <a
                  href={href}
                  className="rounded-control text-ui-sm text-ink-muted transition-colors duration-fast ease-out hover:text-ink"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {signedIn ? (
            <ButtonLink href="/app" variant="primary" size="sm">
              Tableau de bord
            </ButtonLink>
          ) : (
            <>
              <a
                href={loginUrl}
                className="rounded-control px-2 py-1 text-ui-sm text-ink-muted transition-colors duration-fast ease-out hover:text-ink"
              >
                Connexion
              </a>
              <ButtonLink href={startUrl} variant="primary" size="sm">
                Créer mon compte
              </ButtonLink>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            setOpen((value) => !value);
          }}
          aria-expanded={open}
          aria-controls="menu-site"
          className="-mr-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-control text-ink lg:hidden"
        >
          {open ? (
            <X size={22} strokeWidth={1.75} aria-hidden="true" />
          ) : (
            <Menu size={22} strokeWidth={1.75} aria-hidden="true" />
          )}
          <span className="sr-only">Menu</span>
        </button>
      </div>

      <div
        id="menu-site"
        className={cx('border-t border-wall-line lg:hidden', open ? 'block' : 'hidden')}
      >
        <nav aria-label="Sections du site" className="shell py-4">
          <ul className="space-y-1">
            {SECTIONS.map(([href, label]) => (
              <li key={href}>
                <a
                  href={href}
                  onClick={() => {
                    setOpen(false);
                  }}
                  className="flex min-h-[44px] items-center rounded-control text-ui text-ink-muted"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-4 space-y-2">
            {signedIn ? (
              <ButtonLink href="/app" variant="primary" block>
                Tableau de bord
              </ButtonLink>
            ) : (
              <>
                <ButtonLink href={startUrl} variant="primary" block>
                  Créer mon compte
                </ButtonLink>
                <ButtonLink href={loginUrl} variant="secondary" block>
                  Connexion
                </ButtonLink>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
